import React from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { motion } from "framer-motion";
import { MainContext } from "../App";
import { getColorShades } from "../@shared/utils/Utils";
import AudioManager from "../@shared/utils/AudioManager";

export default function NeoButton({
  onClick,
  width,
  height,
  text,
  subText,
  color,
  isIcon,
  icon,
  startAdorn,
  endAdorn,
  disabled,
  borderRadius,
}) {
  // Determine border radius based on whether the button is icon-based
  const { Theme } = React.useContext(MainContext);

  // Motion animation settings for a smooth hover and tap effect
  const motionProps = {
    whileHover: { scale: 1.01 },
    whileTap: { scale: 0.99 },
  };

  const getColors = (color) => {
    if (color === "$primary" || color === "$secondary") {
      return {
        light: `${color}Active`,
        dark: `${color}Hover`,
        shadow: `${color}`,
      };
    }

    return getColorShades(color);
  };

  const handleClick = () => {
    console.log(AudioManager.sfxBank["playerHit"]?.src);
    if (onClick) {
      onClick();
      AudioManager.playSFX("buttonPress");
    }
  };

  return (
    <>
      {isIcon ? (
        <motion.div
          {...motionProps}
          style={{
            border: "4px solid #000",
            width: `${width ? width + "%" : 50 + "px"}`,
            borderRadius: `${borderRadius ? borderRadius : isIcon ? 40 : 10}px`,
            display: "inline-block",
            backgroundColor: "#000",
          }}
        >
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            sx={{
              backgroundColor: color,
              color: "white",
              width: `${width ? width + "%" : 50 + "px"}`, // Adjusted for icon button size (similar to iconSize 36 * 2)
              height: "50px",
              borderRadius: `${
                borderRadius ? borderRadius : isIcon ? 40 : 10
              }px`,
              borderTop: `4px solid ${getColors(color).light}`,
              borderLeft: `4px solid ${getColors(color).light}`,
              borderRight: `4px solid ${getColors(color).dark}`,
              borderBottom: `4px solid ${getColors(color).dark}`,
              boxShadow: "2px 5px 8px rgba(0,0,0,0.8)",
              // You can customize hover styles further if needed:
              "&:hover": {
                backgroundColor: color,
              },
            }}
          >
            {icon}
          </IconButton>
        </motion.div>
      ) : (
        <motion.div
          {...motionProps}
          style={{
            border: "4px solid #000",
            width: `${width}%`,
            borderRadius: `${borderRadius ? borderRadius : 10}px`,
            display: "inline-block",
            backgroundColor: "#000",
            height: "fit-content",
          }}
        >
          <Button
            onClick={handleClick}
            sx={{
              width: "100%",
              backgroundColor: color,
              borderRadius: `${borderRadius ? borderRadius - 2 : 8}px`, // MUI spacing scale (or use a fixed value like "15px")
              boxShadow: "2px 5px 8px rgba(0,0,0,0.8)",
              color: "white",
              padding: "8px 16px",
              // If you want to simulate different border colors on each side,
              // you could consider adding custom CSS here.
              borderTop: `4px solid ${getColors(color).light}`,
              borderLeft: `4px solid ${getColors(color).light}`,
              borderRight: `4px solid ${getColors(color).dark}`,
              borderBottom: `4px solid ${getColors(color).dark}`,
              "&:hover": {
                backgroundColor: color, // Optionally adjust for hover state
              },
              height: height || "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            disabled={disabled}
          >
            {startAdorn}
            {icon}
            <Typography
              sx={{
                width: "100%",
                textAlign: "center",
                textShadow: ` -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px  1.5px 0 #000, 1.5px  1.5px 0 #000`,
                fontSize: ".85rem", // Approximation of "$lg"
                fontWeight: 900,
              }}
            >
              {text}
            </Typography>
            {subText && (
              <Typography
                variant="caption"
                sx={{
                  textShadow: ` -1px -1px 0 #000, 1px -1px 0 #000, -1px  1px 0 #000, 1px  1px 0 #000`,
                  position: "absolute",
                  top: icon ? "70%" : "60%",
                  left: "0%",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {subText}
              </Typography>
            )}
            {endAdorn}
          </Button>
        </motion.div>
      )}
    </>
  );
}
